import type { Document } from '@prisma/client';
import { DocumentType, FileType } from '@prisma/client';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { convertUrlToBlob } from '~/utils/browserFileHandling';
import type { trpc } from './trpc';

type ClientImageEdits = {
	resize?: {
		width: number;
		height: number;
		fit: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
	};
};

export const generateClientImageUrl = (document: Pick<Document, 'bucket' | 'key'>, edits?: ClientImageEdits) => {
	const buffer = Buffer.from(
		JSON.stringify({
			bucket: document.bucket,
			key: document.key,
			edits,
		})
	);
	return `https://d272jmvoweunhk.cloudfront.net/${buffer.toString('base64')}`;
};

export const getImageDimensions: (imageUrl: string) => Promise<[number, number]> = (imageUrl) => {
	return new Promise((resolve, reject) => {
		setTimeout(() => reject('Timed out loading image'), 5000);

		const image = new Image();
		image.addEventListener('load', () => {
			resolve([image.width, image.height]);
		});
		image.src = imageUrl;
	});
};

export const compressImage = async (imageUri: string) => {
	const blob = await convertUrlToBlob(imageUri);

	return await imageCompression(blob as File, { fileType: 'image/png', alwaysKeepResolution: true });
};

export const cropCanvasRegion = async (
	canvas: HTMLCanvasElement,
	cropX: number,
	cropY: number,
	cropWidth: number,
	cropHeight: number
): Promise<{ blob: Blob; base64: string }> => {
	const cropCanvas = document.createElement('canvas');
	const cropCtx = cropCanvas.getContext('2d');
	if (!cropCtx) throw new Error('Failed to get crop canvas context');

	cropCanvas.width = cropWidth;
	cropCanvas.height = cropHeight;

	cropCtx.drawImage(canvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

	const blob = await new Promise<Blob>((resolve, reject) => {
		cropCanvas.toBlob((b) => {
			if (b) resolve(b);
			else reject(new Error('Failed to create blob'));
		}, 'image/png');
	});

	const base64 = await new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result as string;
			const base64Data = result.split(',')[1];

			if (!base64Data) {
				reject(new Error('Failed to convert blob to base64'));
				return;
			}
			resolve(base64Data);
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});

	return { blob, base64 };
};

type LinkImagesArgs = {
	measurementId?: number;
	jobId: number;
	documentTypes?: DocumentType[];
	fileTypes?: FileType[];
	images: UploadImageReturn[];
	client: ReturnType<typeof trpc.useUtils>['client'];
	checklistResponseId?: number;
};

export const linkImages = ({ images, measurementId, jobId, documentTypes, fileTypes, client, checklistResponseId }: LinkImagesArgs) => {
	const createArgs = images.map((image, idx) => {
		return {
			jobId: jobId,
			measurementId: measurementId,
			fileType: fileTypes?.[idx] ?? FileType.JPEG,
			documentType: documentTypes?.[idx] ?? DocumentType.MEASUREMENT_PHOTO,
			size: image.size,
			location: image.s3Destination.s3Location,
			bucket: image.s3Destination.s3Bucket,
			key: image.s3Destination.s3Key,
			name: image.fileName,
			checklistResponseId,
		};
	});
	client.documents.createMany.mutate(createArgs);
};

export type UploadImageReturn = {
	s3Response: Response;
	fileName: string;
	size: number;
	s3Destination: {
		preSignedUploadUrl: string;
		fileName: string;
		s3Location: string;
		s3Bucket: string;
		s3Key: string;
	};
};

export const uploadImage: (
	preview: string,
	businessId: number | undefined,
	customerId: number | undefined,
	jobId: number,
	trpcClient: ReturnType<typeof trpc.useUtils>['client'],
	basename?: string,
	uploadCb?: (percent: number) => void
) => Promise<UploadImageReturn | undefined> = async (preview, businessId, customerId, jobId, trpcClient, basename = 'measurementPhoto', uploadCb) => {
	if (!preview) {
		return;
	}
	const imageData = await convertUrlToBlob(preview);
	//going for "uniquish" here
	const fileName = `${basename}_${businessId}_${customerId}_${jobId}_${new Date().getTime()}.jpeg`;

	const s3Destination = await trpcClient.documents.generatePreSignedUrl.mutate({
		fileName: fileName,
		jobId: jobId,
	});

	return axios
		.put(s3Destination.preSignedUploadUrl, imageData, {
			headers: {
				'Content-Type': 'application/octet-stream',
			},
			onUploadProgress(progressEvent) {
				uploadCb && progressEvent.progress && uploadCb(Math.round(progressEvent.progress * 100));
			},
		})
		.then((response) => {
			return {
				s3Response: response.data,
				fileName,
				size: imageData.size,
				s3Destination,
			};
		});
};
